import { Drawer } from "@components/ui/Drawer"
import { useFlyingCarpet } from "./useFlyingCarpet"
import { FLYING_CARPET_PANELS, FlyingCarpetTabInfo } from "./carpet-panels"
import { AnimatePresence, AnimatePresenceProps, AnimationProps, motion } from "motion/react"
import { PropsWithChildren, useMemo } from "react"
import { Button } from "@components/ui/Button"
import { tv } from "@utils/tw"

export function FlyingCarpetMobile() {
  const carpet = useFlyingCarpet()

  const panel = carpet.tab ? FLYING_CARPET_PANELS[carpet.tab] : null

  const styles = createStyles()

  return (
    <Drawer
      padded={false}
      forceOpen={carpet.isOpen}
      onOpenChange={(open) => !open && carpet.close()}
      className="top-flyingCarpetTop"
      classes={{
        content: styles.content(),
        overlay: styles.overlay(),
        handle: styles.handle(),
        inner: styles.inner(),
        scroller: styles.scroller(),
      }}
      repositionInputs={false}
    >
      <div className="absolute inset-0 outline-none [--padding:var(--tw-grid-margin)]">
        {panel && (
          <div className="relative w-full h-full flex flex-col">
            {/* Top bar */}
            <AnimatePresence mode="wait" initial={false}>
              {panel.hasHeader !== false && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="type-title-m h-[calc(var(--spacing-headerRowHeight)+1px)] pl-[--padding] pr-3 relative border-b border-bg-mid flex-none overflow-hidden flex items-center"
                >
                  {/* Title */}
                  <Ticker tickerKey={panel.title} className="flex flex-1 items-center h-full">
                    <div className="h-full flex items-center">{panel.title}</div>
                  </Ticker>

                  <div className="flex flex-1 items-center justify-end gap-2">
                    <AnimatePresence>
                      {panel.widget && (
                        <motion.div
                          key={panel.title}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <panel.widget />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Close Button */}
                    <Drawer.Close asChild>
                      <Button icon="cross" variant="ghost" shape="icon" />
                    </Drawer.Close>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main section */}
            <div className="flex-1 overflow-hidden flex items-center relative">
              <Ticker tickerKey={carpet.tab!} animation="fade" className="absolute inset-0">
                <FlyingCarpetContent tab={panel} />
              </Ticker>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  )
}

const animationStyles = {
  ticker: {
    initial: { transform: "translateY(-60px)" },
    animate: { transform: "translateY(0px)" },
    exit: { transform: "translateY(60px)" },
    transition: { type: "spring", stiffness: 100, damping: 20 },
  },
  fade: {
    initial: { opacity: 0, y: "-1%", scale: 0.95 },
    animate: { opacity: 1, y: "0%", scale: 1, transition: { bounce: 0.2, delay: 0.1 } },
    exit: { opacity: 0, y: "1%", scale: 0.95, transition: { duration: 0.1 } },
  },
} satisfies Record<string, AnimationProps>

function Ticker(
  props: PropsWithChildren<{
    tickerKey: string | number
    mode?: AnimatePresenceProps["mode"]
    animation?: keyof typeof animationStyles
    className?: string
  }>,
) {
  const animProps = animationStyles[props.animation ?? "ticker"]

  const animationKey = useMemo(() => props.tickerKey + "_" + Date.now(), [props.tickerKey])

  return (
    <div className={props.className}>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div key={animationKey} {...animProps} className="w-full h-full">
          {props.children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function FlyingCarpetContent(props: { tab: FlyingCarpetTabInfo }) {
  const Component = props.tab.component

  if (Component) {
    return <Component />
  } else {
    return null
  }
}

const createStyles = tv({
  slots: {
    overlay: "z-[30]",
    content: ["border-t border-t-bg-mid overflow-hidden rounded-none"],
    handle: "",
    inner: "",
    scroller: "",
  },
})
